"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Icon } from "@/components/formatters/MXPIcon";
import { MarketplaceCard, type MarketplaceCardProps } from "@/components/client/MarketplaceCard/MarketplaceCard";
import { FilterableCard, type FilterableCardProps } from "./FilterableCard";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { layoutHeaderClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
import { darkTextureWrapper, colorSettingDetection, renderHeaderConditionally } from "../../utils/functions";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
import { FilterSidebar } from "./FilterSidebar";
import { Pagination } from "./Pagination";
import { MobileFilterDrawer } from "./MobileFilterDrawer";
import type { FilterGroup, SortOption } from "./types";
import type { FilterableCardsLabelsFields } from "@/queries/fragments/filterableCardsLabelsFragment";
export type { FilterGroup, SortOption, FilterOption } from "./types";
export type { FilterableCardProps } from "./FilterableCard";
import "./LayoutFilterableCards.css";

/**
 * Display type values authored in Contentful on `layoutFilterableCards`.
 * Determines which card component (and matching prop shape) the grid renders.
 */
export type LayoutFilterableCardsDisplayType = "Filterable Card" | "Page: General";

/**
 * One grid item. The shape depends on the layout's {@link LayoutFilterableCardsProps.displayType}:
 * - `"Filterable Card"` → {@link FilterableCardProps} (renders {@link FilterableCard})
 * - `"Page: General"` (default) → {@link MarketplaceCardProps} (renders {@link MarketplaceCard})
 */
export type LayoutFilterableCardItem = FilterableCardProps | MarketplaceCardProps;
export interface LayoutFilterableCardsProps {
  eyebrow?: string;
  header?: string;
  headerElement?: "h1" | "h2";
  subheader?: string;
  description?: string;
  buttons?: TerraLink[];
  presetTheme?: "t2-theme-one" | "t2-theme-two" | "t2-theme-three" | "t2-theme-four" | "t2-theme-five" | "t2-theme-six" | "t2-theme-seven";
  isHeaderBig?: boolean;
  isWrapped?: boolean;

  /**
   * Opt-in: drops the 1440px outer max-width, reduces large-screen horizontal
   * padding, and unlocks a 4-column grid at the `2xl` breakpoint (≥1536px).
   * Intended for the in-product iframe embed where the host page (e.g.
   * Trimble Connect) wants the catalog to fill more horizontal space on large
   * monitors. Defaults to false so public marketplace pages and Storybook
   * stories keep their existing capped layout.
   */
  fullWidth?: boolean;

  /**
   * Default {@link MarketplaceCard} image mode when a card omits `imageDisplayMode`.
   * @default "logo"
   */
  imageDisplayMode?: "logo" | "teaser";
  cards?: LayoutFilterableCardItem[];
  totalResults: number;
  filterGroups?: FilterGroup[];
  activeFilters?: Record<string, string[]>;
  onFilterChange?: (groupId: string, values: string[]) => void;
  onClearAllFilters?: () => void;
  sortOptions?: SortOption[];
  currentSort?: string;
  onSortChange?: (value: string) => void;
  currentPage?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;

  /**
   * Optional client-side search input rendered above the grid. When
   * `onSearchChange` is omitted the input is not rendered.
   */
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  searchPlaceholder?: string;
  isLCP?: boolean;
  /**
   * Selects which card component renders inside the grid.
   * - `"Filterable Card"` → {@link FilterableCard} (cards typed as {@link FilterableCardProps})
   * - `"Page: General"` (default) → {@link MarketplaceCard} (cards typed as {@link MarketplaceCardProps})
   */
  displayType?: LayoutFilterableCardsDisplayType;
  /** Filterable cards labels from `siteConfig.filterableCardsLabels`. */
  labels?: FilterableCardsLabelsFields | null;
}
const CARD_WRAP = "flex h-full w-full mb-6 max-w-[450px] justify-center items-stretch";
const SORT_SELECT = "border border-gray-m3 rounded-lg px-3 py-2 text-sm bg-white text-gray-m9 cursor-pointer min-w-[200px] appearance-none";
export const LayoutFilterableCards = t0 => {
  const $ = _c(107);
  const {
    eyebrow,
    header,
    headerElement: t1,
    subheader,
    description,
    buttons,
    presetTheme,
    isWrapped,
    fullWidth: t2,
    imageDisplayMode: t3,
    cards: t4,
    totalResults,
    filterGroups: t5,
    activeFilters: t6,
    onFilterChange,
    onClearAllFilters,
    sortOptions: t7,
    currentSort,
    onSortChange,
    currentPage: t8,
    pageSize: t9,
    pageSizeOptions,
    onPageChange,
    onPageSizeChange,
    searchTerm,
    onSearchChange,
    searchPlaceholder,
    isLCP: t10,
    displayType,
    labels
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const fullWidth = t2 === undefined ? false : t2;
  const imageDisplayMode = t3 === undefined ? "logo" : t3;
  let t11;
  if ($[0] !== t4) {
    t11 = t4 === undefined ? [] : t4;
    $[0] = t4;
    $[1] = t11;
  } else {
    t11 = $[1];
  }
  const cards = t11;
  let t12;
  if ($[2] !== t5) {
    t12 = t5 === undefined ? [] : t5;
    $[2] = t5;
    $[3] = t12;
  } else {
    t12 = $[3];
  }
  const filterGroups = t12;
  let t13;
  if ($[4] !== t6) {
    t13 = t6 === undefined ? {} : t6;
    $[4] = t6;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  const activeFilters = t13;
  let t14;
  if ($[6] !== t7) {
    t14 = t7 === undefined ? [] : t7;
    $[6] = t7;
    $[7] = t14;
  } else {
    t14 = $[7];
  }
  const sortOptions = t14;
  const currentPage = t8 === undefined ? 1 : t8;
  const pageSize = t9 === undefined ? 30 : t9;
  const isLCP = t10 === undefined ? false : t10;
  const resolvedSearchPlaceholder = searchPlaceholder ?? (labels?.labelForSearchApps || "Search apps...");
  const {
    width
  } = useScreenSize();
  const isMobile = width > 0 && width < 1024;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("filter");
  const wrapperClasses = isWrapped ? fullWidth ? "w-full px-5 lg:px-12 m-auto" : "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : "";
  const gridClasses = fullWidth ? "w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 md:gap-x-6 xl:gap-x-8 2xl:gap-x-10 gap-y-4" : "w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:gap-x-6 xl:gap-x-8 2xl:gap-x-10 gap-y-4";
  const t15 = presetTheme || "";
  let t16;
  if ($[8] !== t15) {
    t16 = colorSettingDetection(t15);
    $[8] = t15;
    $[9] = t16;
  } else {
    t16 = $[9];
  }
  const colorSetting = t16;
  const mainClasses = `py-16 lg:py-[100px] ${presetTheme || ""} ${colorSetting}`;
  const headerClasses = `${layoutHeaderClasses} mb-4! `;
  const t17 = header || "";
  let t18;
  if ($[10] !== headerElement || $[11] !== t17) {
    t18 = renderHeaderConditionally(headerClasses, t17, headerElement);
    $[10] = headerElement;
    $[11] = t17;
    $[12] = t18;
  } else {
    t18 = $[12];
  }
  const finalHeader = t18;
  const t19 = presetTheme || "";
  let t20;
  if ($[13] !== t19) {
    t20 = darkTextureWrapper(t19);
    $[13] = t19;
    $[14] = t20;
  } else {
    t20 = $[14];
  }
  const componentStyles = t20;
  const hasFilters = filterGroups.length > 0;
  const hasHeader = eyebrow || header || subheader || description;
  const isFilterableVariant = displayType === "Filterable Card";
  let t21;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = mode => {
      setDrawerMode(mode);
      setDrawerOpen(true);
    };
    $[15] = t21;
  } else {
    t21 = $[15];
  }
  const openDrawer = t21;
  let t22;
  if ($[16] !== imageDisplayMode || $[17] !== isFilterableVariant || $[18] !== isLCP || $[19] !== labels?.labelForFeatured || $[20] !== labels?.labelForFeaturedCard) {
    t22 = (card, index) => <div key={`card-${index}`} className={CARD_WRAP} data-testid="filterable-card">{isFilterableVariant ? <FilterableCard {...card as FilterableCardProps} featuredLabel={labels?.labelForFeatured || "Featured"} featuredCardAriaLabel={labels?.labelForFeaturedCard || "Featured card"} /> : <MarketplaceCard {...card as MarketplaceCardProps} imageDisplayMode={(card as MarketplaceCardProps).imageDisplayMode ?? imageDisplayMode} isLCP={isLCP} />}</div>;
    $[16] = imageDisplayMode;
    $[17] = isFilterableVariant;
    $[18] = isLCP;
    $[19] = labels?.labelForFeatured;
    $[20] = labels?.labelForFeaturedCard;
    $[21] = t22;
  } else {
    t22 = $[21];
  }
  const renderCard = t22;
  let t23;
  if ($[22] !== buttons || $[23] !== currentSort || $[24] !== description || $[25] !== eyebrow || $[26] !== finalHeader || $[27] !== hasHeader || $[28] !== isMobile || $[29] !== labels?.labelForResult || $[30] !== labels?.labelForResults || $[31] !== labels?.labelForSortBy || $[32] !== onSearchChange || $[33] !== onSortChange || $[34] !== sortOptions || $[35] !== subheader || $[36] !== totalResults) {
    t23 = hasHeader && <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between pb-8 lg:pb-12 gap-4"><div className="flex-1">{eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}<div className="flex items-baseline gap-2 flex-wrap">{finalHeader}{totalResults > 0 && !onSearchChange && <span className="text-gray-m5 t2-dark:text-gray-m3 text-lg font-normal">({totalResults}{" "}{totalResults === 1 ? labels?.labelForResult || "Result" : labels?.labelForResults || "Results"})</span>}</div>{subheader && <div className={layoutSubheaderClasses}>{subheader}</div>}{description && <CommonDescription description={description} className="t2-layout__description mt-2" />}{buttons && buttons.length > 0 && <div className="flex gap-x-4 gap-y-3 flex-wrap mt-4">{buttons.map(_temp)}</div>}</div>{sortOptions.length > 0 && !isMobile && <div className="shrink-0 flex items-center gap-2"><label htmlFor="filterable-sort" className="text-sm text-gray-m7 t2-dark:text-gray-m3 whitespace-nowrap">{labels?.labelForSortBy || "Sort By"}:</label><div className="relative"><select id="filterable-sort" className={SORT_SELECT} value={currentSort} onChange={e => onSortChange?.(e.target.value)}>{sortOptions.map(_temp2)}</select><Icon type="t2u-chevron-right" size={10} rotateDeg="90" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-m5 t2-dark:text-gray-m3" /></div></div>}</div>;
    $[22] = buttons;
    $[23] = currentSort;
    $[24] = description;
    $[25] = eyebrow;
    $[26] = finalHeader;
    $[27] = hasHeader;
    $[28] = isMobile;
    $[29] = labels?.labelForResult;
    $[30] = labels?.labelForResults;
    $[31] = labels?.labelForSortBy;
    $[32] = onSearchChange;
    $[33] = onSortChange;
    $[34] = sortOptions;
    $[35] = subheader;
    $[36] = totalResults;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== activeFilters || $[39] !== filterGroups || $[40] !== hasFilters || $[41] !== isMobile || $[42] !== labels || $[43] !== onClearAllFilters || $[44] !== onFilterChange) {
    t24 = hasFilters && !isMobile && <FilterSidebar filterGroups={filterGroups} activeFilters={activeFilters} onFilterChange={(groupId, values) => onFilterChange?.(groupId, values)} onClearAll={() => onClearAllFilters?.()} labels={labels} />;
    $[38] = activeFilters;
    $[39] = filterGroups;
    $[40] = hasFilters;
    $[41] = isMobile;
    $[42] = labels;
    $[43] = onClearAllFilters;
    $[44] = onFilterChange;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== labels?.labelForResult || $[47] !== labels?.labelForResults || $[48] !== onSearchChange || $[49] !== resolvedSearchPlaceholder || $[50] !== searchTerm || $[51] !== totalResults) {
    t25 = (onSearchChange || totalResults > 0) && <div className="flex items-center justify-between gap-4 mb-6">{onSearchChange ? <div className="relative flex-1 max-w-[480px]"><Icon type="t2u-search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-m5 t2-dark:text-gray-m3" /><input type="text" role="searchbox" aria-label={resolvedSearchPlaceholder} placeholder={resolvedSearchPlaceholder} value={searchTerm ?? ""} onChange={e_0 => onSearchChange(e_0.target.value)} className="w-full pl-9 pr-3 py-2 text-sm bg-white t2-dark:bg-gray-m9 text-gray-m9 t2-dark:text-white placeholder-gray-m5 border border-gray-m3 t2-dark:border-gray-m7 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-trmb focus:border-blue-trmb" /></div> : <span aria-hidden={true} />}{totalResults > 0 && <span className="text-sm text-gray-m5 t2-dark:text-gray-m3 whitespace-nowrap">{totalResults}{" "}{totalResults === 1 ? labels?.labelForResult || "Result" : labels?.labelForResults || "Results"}</span>}</div>;
    $[46] = labels?.labelForResult;
    $[47] = labels?.labelForResults;
    $[48] = onSearchChange;
    $[49] = resolvedSearchPlaceholder;
    $[50] = searchTerm;
    $[51] = totalResults;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== cards || $[54] !== gridClasses || $[55] !== labels?.labelForNoFilterResults || $[56] !== renderCard) {
    t26 = cards.length === 0 ? <div className="py-16 text-center text-gray-m5 t2-dark:text-gray-m3 text-lg">{labels?.labelForNoFilterResults || "No results found. Try adjusting your filters."}</div> : <div className={gridClasses}>{cards.map((card_0, index_0) => renderCard(card_0, index_0))}</div>;
    $[53] = cards;
    $[54] = gridClasses;
    $[55] = labels?.labelForNoFilterResults;
    $[56] = renderCard;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== currentPage || $[59] !== labels || $[60] !== onPageChange || $[61] !== onPageSizeChange || $[62] !== pageSize || $[63] !== pageSizeOptions || $[64] !== totalResults) {
    t27 = totalResults > 0 && onPageChange && onPageSizeChange && <Pagination currentPage={currentPage} pageSize={pageSize} totalResults={totalResults} pageSizeOptions={pageSizeOptions} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} labels={labels} />;
    $[58] = currentPage;
    $[59] = labels;
    $[60] = onPageChange;
    $[61] = onPageSizeChange;
    $[62] = pageSize;
    $[63] = pageSizeOptions;
    $[64] = totalResults;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t25 || $[67] !== t26 || $[68] !== t27) {
    t28 = <div className="flex-1 min-w-0">{t25}{t26}{t27}</div>;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t24 || $[71] !== t28) {
    t29 = <div className="flex gap-5 lg:gap-10 flex-wrap">{t24}{t28}</div>;
    $[70] = t24;
    $[71] = t28;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== hasFilters || $[74] !== isMobile || $[75] !== labels?.labelForFilter || $[76] !== labels?.labelForSort || $[77] !== sortOptions) {
    t30 = isMobile && (hasFilters || sortOptions.length > 0) && <div className="fixed bottom-0 left-0 right-0 z-40 flex flex-col gap-2 p-4 bg-white border-t border-gray-m2 shadow-lg">{hasFilters && <button className="w-full py-3 bg-blue-trmb text-white text-sm font-bold uppercase tracking-wider rounded-lg border-none cursor-pointer flex items-center justify-center gap-2 hover:bg-blue-navy" onClick={() => openDrawer("filter")}><Icon type="t2u-collapse-close" size={14} />{labels?.labelForFilter || "Filter"}</button>}{sortOptions.length > 0 && <button className="w-full py-3 bg-white text-gray-m9 text-sm font-bold uppercase tracking-wider rounded-lg border border-gray-m3 cursor-pointer flex items-center justify-center gap-2 hover:bg-gray-m1" onClick={() => openDrawer("sort")}><Icon type="t2u-chevron-right" size={10} rotateDeg="270" /><Icon type="t2u-chevron-right" size={10} rotateDeg="90" />{labels?.labelForSort || "Sort"}</button>}</div>;
    $[73] = hasFilters;
    $[74] = isMobile;
    $[75] = labels?.labelForFilter;
    $[76] = labels?.labelForSort;
    $[77] = sortOptions;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  const t31 = currentSort || "";
  let t32;
  if ($[79] !== onFilterChange) {
    t32 = (groupId_0, values_0) => onFilterChange?.(groupId_0, values_0);
    $[79] = onFilterChange;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== onSortChange) {
    t33 = value => onSortChange?.(value);
    $[81] = onSortChange;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== onClearAllFilters) {
    t34 = () => onClearAllFilters?.();
    $[83] = onClearAllFilters;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setDrawerOpen(false);
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== activeFilters || $[87] !== drawerMode || $[88] !== drawerOpen || $[89] !== filterGroups || $[90] !== labels || $[91] !== sortOptions || $[92] !== t31 || $[93] !== t32 || $[94] !== t33 || $[95] !== t34) {
    t36 = <MobileFilterDrawer isOpen={drawerOpen} mode={drawerMode} filterGroups={filterGroups} activeFilters={activeFilters} sortOptions={sortOptions} currentSort={t31} onFilterChange={t32} onSortChange={t33} onClearAll={t34} onClose={t35} labels={labels} />;
    $[86] = activeFilters;
    $[87] = drawerMode;
    $[88] = drawerOpen;
    $[89] = filterGroups;
    $[90] = labels;
    $[91] = sortOptions;
    $[92] = t31;
    $[93] = t32;
    $[94] = t33;
    $[95] = t34;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  let t37;
  if ($[97] !== t23 || $[98] !== t29 || $[99] !== t30 || $[100] !== t36 || $[101] !== wrapperClasses) {
    t37 = <div className={wrapperClasses}>{t23}{t29}{t30}{t36}</div>;
    $[97] = t23;
    $[98] = t29;
    $[99] = t30;
    $[100] = t36;
    $[101] = wrapperClasses;
    $[102] = t37;
  } else {
    t37 = $[102];
  }
  let t38;
  if ($[103] !== componentStyles || $[104] !== mainClasses || $[105] !== t37) {
    t38 = <div className={mainClasses} style={componentStyles} data-testid="layout-filterable-cards">{t37}</div>;
    $[103] = componentStyles;
    $[104] = mainClasses;
    $[105] = t37;
    $[106] = t38;
  } else {
    t38 = $[106];
  }
  return t38;
};
function _temp(btn, i) {
  return <TerraLink key={`btn-${btn.label}-${i}`} {...btn} />;
}
function _temp2(opt) {
  return <option key={opt.value} value={opt.value}>{opt.label}</option>;
}